import React, { useState, useEffect } from 'react';
import { generateOTP, verifyOTP, verifyPIN, setupPIN, redeemReferralCode } from '../services/api';
import api from '../services/api';
import { Typography, TextField, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MuiTelInput } from 'mui-tel-input';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PinInput from '../components/PinInput';
import { Close as CloseIcon } from '@mui/icons-material';
import { toE164 } from '../utils/phoneUtils';
import { trackRedditSignUp } from '../services/redditPixel';
import { captureAttributionFromCurrentUrl, postAttributionFromStorage, trackProductEvent } from '../services/analyticsService';
import { postAuthDestination, WEB_SIGNUP_HANDOFF_PATH } from '../utils/onboardingHandoff';
import { getAccessToken } from '../utils/accessToken';
import { trackLinkedInWebSignup } from '../services/linkedInInsightTag';
import { trackXWebSignup } from '../services/xAdsPixel';
import { trackMetaWebSignup } from '../services/metaPixel';

// Simple no-op transition to avoid Material-UI/React 18 compatibility issues
const NoTransition = React.forwardRef(({ children, in: inProp }, ref) => {
  return inProp ? <div ref={ref} style={{ display: 'contents' }}>{children}</div> : null;
});
NoTransition.displayName = 'NoTransition';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: 500,
    overflowX: 'hidden'
  },
  '& .MuiDialogContent-root': {
    overflowX: 'hidden'
  }
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: '9999px',
  padding: '4px',
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 0,
    borderRadius: '9999px !important',
    flex: 1,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.95rem',
    padding: '10px 16px',
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    zIndex: 1,
    transition: 'color 0.25s ease',
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      fontWeight: 600,
      '&:hover': {
        backgroundColor: 'transparent',
      }
    },
    '&:hover': {
      backgroundColor: 'transparent',
    }
  }
}));

const ToggleSlider = styled('div')(({ theme, position }) => ({
  position: 'absolute',
  top: '4px',
  left: position === 'phone' ? '4px' : 'calc(50% + 2px)',
  width: 'calc(50% - 6px)',
  height: 'calc(100% - 8px)',
  backgroundColor: '#fff',
  borderRadius: '9999px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  transition: 'left 0.25s ease',
  zIndex: 0,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '9999px',
    backgroundColor: theme.palette.grey[100],
    paddingLeft: theme.spacing(2),
    '&.Mui-focused': {
      backgroundColor: theme.palette.grey[100],
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2
      }
    },
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    },
    '&.Mui-focused:hover': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiOutlinedInput-input': {
    paddingLeft: theme.spacing(1),
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.7
    }
  }
}));

const Login = ({ open = true, onClose = () => {} }) => {
  const [inputValue, setInputValue] = useState('');
  const [inputMode, setInputMode] = useState('phone'); // 'phone' or 'email'
  const [otp, setOtp] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoVerifying, setAutoVerifying] = useState(false);
  const navigate = useNavigate();
  const { login, updateUser, loadSubscription, fetchUserProfile } = useAuth();

  // Helper function to handle pending referral code redemption
  const handlePendingReferralCode = async () => {
    const pendingCode = sessionStorage.getItem('pendingReferralCode');
    if (pendingCode) {
      try {
        await redeemReferralCode(pendingCode);
        sessionStorage.removeItem('pendingReferralCode');
        console.log('Referral code applied successfully:', pendingCode);
      } catch (err) {
        console.error('Failed to apply referral code:', err);
        // Don't fail the login process if referral fails
      }
    }
  };

  /** #1221: unfinished web accounts finish setup in the mobile app, not /activity. */
  const finishAuthAndNavigate = React.useCallback(async (verifiedUser) => {
    const destination = postAuthDestination(verifiedUser);
    if (destination === WEB_SIGNUP_HANDOFF_PATH) {
      trackProductEvent('web_signup_app_handoff', {
        currentStep: verifiedUser?.onboarding?.currentStep || 'welcome',
      }).catch(() => {});
      // validateToken starts this flush without waiting. The download page
      // then replaces the document with the store, which cancels an in-flight
      // axios call. Wait for the same flush, but only briefly: a stalled
      // request must not trap a verified signup in this dialog. Storage is
      // cleared only after a successful post, so a timeout leaves the tags
      // for a later flush.
      let timer;
      try {
        await Promise.race([
          postAttributionFromStorage().catch(() => {}),
          new Promise((resolve) => {
            timer = setTimeout(resolve, 2000);
          }),
        ]);
      } catch (_) {
        // Stored tags remain for a later signed-in flush.
      } finally {
        clearTimeout(timer);
      }
    }
    onClose();
    navigate(destination);
  }, [navigate, onClose]);

  // Main submit handler
  const handleSubmit = React.useCallback(async (e, force = false) => {
    if (e) e.preventDefault();
    if (isSubmitting && !force) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      if (step === 1) {
        // Generate OTP with email or phone
        const payload = inputMode === 'email'
          ? { email: inputValue.trim() }
          : { phone: toE164(inputValue) };
        
        await generateOTP(payload);
        setStep(2);
      } else if (step === 2) {
        // Ensure identifier is preserved from step 1
        if (!inputValue) {
          setError('Contact information is missing. Please start over.');
          setStep(1);
          return;
        }
        
        // Verify OTP
        const identifier = inputMode === 'email'
          ? { email: inputValue.trim() }
          : { phone: toE164(inputValue) };
        
        const response = await verifyOTP(identifier, otp);
        if (response.token) {
          // Access token is held in memory by verifyOTP (TM-011).
          
          if (response.needsPinVerification) {
            // Existing user needs to verify PIN on new device
            setStep(3);
            setError(''); // Clear any existing errors
          } else if (!response.user.isPinSetup) {
            // New user needs to set up PIN
            setStep(4);
          } else {
            // User is verified and device is trusted
            const verifiedUser = await login(response.token, inputValue);
            await handlePendingReferralCode();
            await finishAuthAndNavigate(verifiedUser);
          }
        } else {
          setError('Invalid verification code. Please try again.');
        }
      } else if (step === 3) {
        // PIN verification for new device
        if (!(/^\d{4}$/).test(pin)) {
          setError('Please enter a valid 4-digit PIN.');
          return;
        }
        
        try {
          const response = await verifyPIN(inputValue, pin);
          if (!response.token) {
            setError('PIN verification failed. Please try again.');
            return;
          }

          api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;
          
          try {
            // Establish session with the new token
            const verifiedUser = await login(response.token, inputValue);
            
            // Fetch user profile and subscription data
            await fetchUserProfile();
            await loadSubscription(true);
            
            // Handle any pending referral code
            await handlePendingReferralCode();
            
            // Only navigate after auth state is fully established
            await finishAuthAndNavigate(verifiedUser);
          } catch (loginErr) {
            console.error('Error during login after PIN verification:', loginErr);
            throw new Error('Failed to establish session. Please try again.');
          }
        } catch (err) {
          console.error('Error during PIN verification:', err);
          setError(err.message || 'Failed to verify PIN. Please try again.');
        }
      } else if (step === 4) {
        // New PIN setup
        if (!(/^\d{4}$/).test(pin)) {
          setError('Please enter a valid 4-digit PIN.');
          return;
        }
        setStep(5);
      } else if (step === 5) {
        // Confirm new PIN
        if (!(/^\d{4}$/).test(confirmPin)) {
          setError('Please enter a valid 4-digit PIN.');
          return;
        }
        if (pin !== confirmPin) {
          setError('PINs do not match. Please try again.');
          setConfirmPin('');
          return;
        }

        const currentToken = getAccessToken();
        if (!currentToken) {
          throw new Error('Session expired. Please verify your email again.');
        }

        const response = await setupPIN(inputValue, pin);
        if (response.token) {
          const verifiedUser = await login(response.token);
          await updateUser({ isPinSetup: true });
          try {
            trackLinkedInWebSignup();
          } catch (_) {
            // Ad measurement never blocks a new account.
          }
          try {
            trackXWebSignup();
          } catch (_) {
            // Ad measurement never blocks a new account.
          }
          try {
            // Not awaited: hashing the id for the eventID must not delay login.
            trackMetaWebSignup(verifiedUser?._id || verifiedUser?.id).catch(() => {});
          } catch (_) {
            // Ad measurement never blocks a new account.
          }
          await handlePendingReferralCode();
          try {
            trackRedditSignUp();
          } catch (_) {
            // Ad measurement must never surface as a login error.
          }
          // New PIN setup ⇒ brand-new web account; still use verified user so
          // finished-onboarding edge cases (rare) keep activity access.
          await finishAuthAndNavigate(verifiedUser);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.message === 'Session expired. Please verify your email again.' || 
          err.message === 'Session expired. Please verify your phone again.' ||
          err.message === 'No authentication token found. Please log in again.') {
        setStep(1);
        setError('Session expired. Please verify again.');
      } else {
        // Handle API error responses
        const apiError = err.response?.data?.error;
        const errorMessage = (typeof apiError === 'string' ? apiError : apiError?.message) || err.message || 'An error occurred. Please try again.';
        setError(errorMessage);
        
        // If the code has expired, allow requesting a new one
        if (err.response?.data?.shouldRequestNewCode) {
          setOtp('');
        }
        
        // If we've exceeded rate limits, disable the form temporarily
        if (err.response?.status === 429) {
          setIsSubmitting(true);
          const retryAfter = err.response?.data?.retryAfter || 900; // Default to 15 minutes
          setTimeout(() => {
            setIsSubmitting(false);
            setError('');
          }, retryAfter * 1000);
        }
      }
    } finally {
      setIsSubmitting(false);
      setAutoVerifying(false);
    }
  }, [isSubmitting, step, inputValue, inputMode, otp, pin, confirmPin, login, updateUser, fetchUserProfile, loadSubscription, finishAuthAndNavigate]);

  const handleInputModeChange = (event, newMode) => {
    if (newMode !== null) {
      setInputValue('');
      setInputMode(newMode);
    }
  };

  useEffect(() => {
    // App-level capture in App.js is the primary path; this is a defense-in-depth
    // call for users who somehow bypass the App-level effect. First-touch wins,
    // so this never overwrites an earlier capture.
    captureAttributionFromCurrentUrl({ captureMethod: 'login_page' });
  }, []);

  useEffect(() => {
    // Check URL parameters for PIN verification requirement
    const params = new URLSearchParams(window.location.search);
    const storedEmail = params.get('email');
    const requirePin = params.get('requirePin');
    
    if (storedEmail && requirePin === 'true') {
      setInputValue(storedEmail);
      setInputMode('email');
      setStep(3); // Go directly to PIN verification
    }
    
    // Clear URL parameters
    if (requirePin) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const phoneParam = params.get('phone');
    const otpParam = params.get('otp');

    if ((emailParam || phoneParam) && otpParam) {
      setInputValue(emailParam || phoneParam);
      setInputMode(emailParam ? 'email' : 'phone');
      setOtp(otpParam);
      setStep(2);
      setAutoVerifying(true);
      setIsSubmitting(true);

      params.delete('email');
      params.delete('phone');
      params.delete('otp');
      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Stabilize submit reference to avoid effect dependency churn
  const handleSubmitRef = React.useRef(handleSubmit);
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    if (autoVerifying && inputValue && otp && step === 2) {
      setIsSubmitting(false);
      handleSubmitRef.current(undefined, true);
    }
  }, [autoVerifying, inputValue, otp, step]);

  useEffect(() => {
    // Only reset when dialog is closing to avoid clearing auto-verify params on mount
    if (!open) {
      setInputValue('');
      setOtp('');
      setPin('');
      setConfirmPin('');
      setError('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handlePinComplete = () => {
    handleSubmit();
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      TransitionComponent={NoTransition}
      BackdropProps={{
        TransitionComponent: NoTransition,
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={onClose} size="small" disableRipple>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography 
          component="div" 
          sx={{ 
            fontWeight: 'bold', 
            fontSize: '1.75rem', 
            mt: 1 
          }}
        >
          {step === 1 ? 'Welcome' : 
           step === 2 ? 'Enter code' : 
           step === 3 ? 'Enter PIN' :
           step === 4 ? 'Set Your PIN' :
           'Confirm Your PIN'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {step === 1 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 3 }}>
            Enter your phone number or email to get started. If you don't have an account yet, we'll create one for you automatically.
          </Typography>
        )}
        {step === 2 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 3 }}>
            We've sent a verification code to {inputMode === 'email' ? 'your email. Please check your inbox and spam/junk folder.' : 'your phone via SMS.'}
          </Typography>
        )}
        {step === 3 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 3 }}>
            Since you're signing in from a new device, please enter your PIN to verify your identity. After verification, this device will be trusted.
          </Typography>
        )}
        {step === 4 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 3 }}>
            Create a 4-digit PIN to secure your account. You'll need this PIN when accessing sensitive information.
          </Typography>
        )}
        {step === 5 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 3 }}>
            Please re-enter your PIN to confirm. Make sure to remember it - you'll need it to access your account.
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <Box>
            {step === 1 && (
              <>
                <Box sx={{ mb: 3 }}>
                  <StyledToggleButtonGroup
                    value={inputMode}
                    exclusive
                    onChange={handleInputModeChange}
                    aria-label="login method"
                  >
                    <ToggleSlider position={inputMode} />
                    <ToggleButton value="phone" aria-label="phone">
                      Phone
                    </ToggleButton>
                    <ToggleButton value="email" aria-label="email">
                      Email
                    </ToggleButton>
                  </StyledToggleButtonGroup>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 1,
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: 'text.secondary'
                    }}
                  >
                    {inputMode === 'email' ? 'Email Address' : 'Phone Number'}
                  </Typography>
                  
                  {inputMode === 'email' ? (
                    <StyledTextField
                      type="email"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Enter your email"
                      required
                      fullWidth
                      autoFocus
                    />
                  ) : (
                    <MuiTelInput
                      value={inputValue}
                      onChange={setInputValue}
                      defaultCountry="US"
                      preferredCountries={['US', 'CA']}
                      fullWidth
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '9999px',
                          backgroundColor: (theme) => theme.palette.grey[100],
                          '&.Mui-focused': {
                            backgroundColor: (theme) => theme.palette.grey[100],
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: (theme) => theme.palette.primary.main,
                              borderWidth: 2
                            }
                          },
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.grey[200]
                          },
                          '&.Mui-focused:hover': {
                            backgroundColor: (theme) => theme.palette.grey[100]
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        }
                      }}
                    />
                  )}
                </Box>
                
                {error && (
                  <Typography color="error" align="center" sx={{ mt: 2 }}>
                    {error}
                  </Typography>
                )}
              </>
            )}
            {step === 2 && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 1,
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: 'text.secondary'
                    }}
                  >
                    Verification Code
                  </Typography>
                <StyledTextField
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter verification code"
                  required
                  fullWidth
                  autoFocus
                />
              </Box>
              {autoVerifying && (
                <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography align="center">
                    Verifying code...
                  </Typography>
                </Box>
              )}
              {error && (
                <Typography color="error" align="center" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
              </>
            )}
            {step === 3 && (
              <PinInput
                value={pin}
                onChange={setPin}
                label="Enter your PIN"
                error={error}
                autoFocus
                onComplete={handlePinComplete}
              />
            )}
            {step === 4 && (
              <PinInput
                value={pin}
                onChange={setPin}
                label="Enter a new PIN"
                error={error}
                autoFocus
                onComplete={handlePinComplete}
              />
            )}
            {step === 5 && (
              <PinInput
                value={confirmPin}
                onChange={setConfirmPin}
                label="Confirm your PIN"
                error={error}
                autoFocus
                onComplete={handlePinComplete}
              />
            )}
          </Box>
        </form>
      </DialogContent>

      {(step === 1 || step === 2) && (
        <DialogActions sx={{ p: 0, mt: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isSubmitting}
            disableRipple
            sx={{
              borderRadius: '9999px',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 1000,
              fontSize: '1.1rem'
            }}
          >
            {isSubmitting ? 'Please wait...' : step === 1 ? 'Continue' : 'Verify Code'}
          </Button>
        </DialogActions>
      )}
    </StyledDialog>
  );
};

export default Login;
